/**
 * City planning slot visibility — mirrors iOS CityPlanningSlotVisibility.
 */

import type { PlanTimeSlot } from './aggregatedPlanData';
import { PLAN_TIME_SLOTS } from './aggregatedPlanData';
import type { TripType } from '../types/client';
import { addDaysYmd } from './wallClock';

export const ORDERED_DAILY_SLOTS: PlanTimeSlot[] = [...PLAN_TIME_SLOTS];

export function defaultStartHour(slot: PlanTimeSlot): number {
  switch (slot) {
    case 'breakfast':
      return 8;
    case 'morning':
      return 9;
    case 'lunch':
      return 12;
    case 'afternoon':
      return 14;
    case 'dinner':
      return 18;
    case 'evening':
      return 19;
    case 'night':
      return 21;
    default:
      return 9;
  }
}

/**
 * Whether a wall-clock hour is reasonable for the given City Planning slot.
 * Night wraps past midnight (21:00–04:00). Mirrors iOS CityPlanningSlotVisibility.containsHour.
 */
export function containsHour(hour: number, slot: PlanTimeSlot): boolean {
  switch (slot) {
    case 'breakfast':
      return hour >= 6 && hour <= 10;
    case 'morning':
      return hour >= 8 && hour <= 12;
    case 'lunch':
      return hour >= 11 && hour <= 15;
    case 'afternoon':
      return hour >= 13 && hour <= 17;
    case 'dinner':
      return hour >= 17 && hour <= 21;
    case 'evening':
      return hour >= 18 && hour <= 23;
    case 'night':
      return hour >= 21 || hour <= 4;
    default:
      return true;
  }
}

/**
 * If start is outside the slot, snap to the slot default hour and preserve duration.
 * iOS Schedule Activity / City Planning #152.
 */
export function remapStartHmIfOutsideSlot(
  startHm: string,
  endHm: string,
  slot: PlanTimeSlot
): { startTime: string; endTime: string } {
  const match = /^(\d{1,2}):(\d{2})$/.exec(startHm.trim());
  const hour = match ? Number(match[1]) : NaN;
  if (!Number.isFinite(hour) || containsHour(hour, slot)) {
    return { startTime: startHm, endTime: endHm };
  }

  const startMins = (() => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(startHm.trim());
    const e = /^(\d{1,2}):(\d{2})$/.exec(endHm.trim());
    if (!m || !e) return 60;
    const s = Number(m[1]) * 60 + Number(m[2]);
    const en = Number(e[1]) * 60 + Number(e[2]);
    const span = en - s;
    return span > 0 ? span : 60;
  })();

  const defaultHour = defaultStartHour(slot);
  const startTime = `${String(defaultHour).padStart(2, '0')}:00`;
  const endTotal = defaultHour * 60 + startMins;
  const endH = Math.floor(endTotal / 60) % 24;
  const endM = endTotal % 60;
  const endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
  return { startTime, endTime };
}

function hmToMinutes(hm: string | null | undefined): number | null {
  if (!hm || !/^\d{2}:\d{2}$/.test(hm)) return null;
  const [h, m] = hm.split(':').map((p) => Number.parseInt(p, 10));
  return h * 60 + m;
}

/**
 * Cruise: hide shore Breakfast from the day after embarkation through
 * the day after debarkation (inclusive). Embarkation day still shows Breakfast.
 */
export function shouldHideCruiseBreakfast(options: {
  dayDate: string;
  tripType: TripType | null | undefined;
  cruiseEmbarkDay: string | null | undefined;
  cruiseDebarkDay: string | null | undefined;
}): boolean {
  if (options.tripType !== 'cruise') return false;
  const day = options.dayDate;
  const embark = options.cruiseEmbarkDay;
  const debark = options.cruiseDebarkDay;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !embark || !debark) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(embark) || !/^\d{4}-\d{2}-\d{2}$/.test(debark)) {
    return false;
  }
  const hideFrom = addDaysYmd(embark, 1);
  const hideThrough = addDaysYmd(debark, 1);
  return day >= hideFrom && day <= hideThrough;
}

export function requiredSlots(options: {
  dayDate: string;
  hasArrival?: boolean;
  arrivalHm?: string | null;
  hasDeparture?: boolean;
  departureHm?: string | null;
  tripType?: TripType | null;
  cruiseEmbarkDay?: string | null;
  cruiseDebarkDay?: string | null;
}): PlanTimeSlot[] {
  const hideBreakfast = shouldHideCruiseBreakfast({
    dayDate: options.dayDate,
    tripType: options.tripType,
    cruiseEmbarkDay: options.cruiseEmbarkDay,
    cruiseDebarkDay: options.cruiseDebarkDay,
  });

  const arrivalMins = options.hasArrival
    ? hmToMinutes(options.arrivalHm)
    : null;
  const departureMins = options.hasDeparture
    ? hmToMinutes(options.departureHm)
    : null;

  return ORDERED_DAILY_SLOTS.filter((slot) => {
    if (slot === 'breakfast' && hideBreakfast) return false;
    const hour = defaultStartHour(slot);
    const slotMins = hour * 60;
    if (arrivalMins != null && slotMins < arrivalMins) return false;
    if (departureMins != null && slotMins > departureMins) return false;
    return true;
  });
}

export function slotLabel(slot: PlanTimeSlot): string {
  switch (slot) {
    case 'breakfast':
      return 'Breakfast';
    case 'morning':
      return 'Morning';
    case 'lunch':
      return 'Lunch';
    case 'afternoon':
      return 'Afternoon';
    case 'dinner':
      return 'Dinner';
    case 'evening':
      return 'Evening';
    case 'night':
      return 'Night';
    default:
      return slot;
  }
}
